import { Close } from '@mui/icons-material';
import { Avatar, Button, DataTable, SearchBar, Tag } from 'finara-components';
import type { DataTableColumn, TagVariant } from 'finara-components';
import { useState } from 'react';

import { ErrorState, LoadingState } from '@/components/common';
import { useAdminTransactions } from '@/hooks/queries/useAnalytics';
import type { RecentTransaction } from '@/types/analytics';

interface AllTransactionsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const getStatusVariant = (status: string): TagVariant => {
  switch (status) {
    case 'Completado':
      return 'green';
    case 'Procesando':
      return 'blue';
    case 'Pendiente':
      return 'yellow';
    default:
      return 'gray';
  }
};

function getInitials(name: string): string {
  const names = name.split(' ');
  if (names.length >= 2) {
    return `${names[0]?.[0] || ''}${names[1]?.[0] || ''}`.toUpperCase();
  }
  return name.substring(0, 2).toUpperCase();
}

export const AllTransactionsModal: React.FC<AllTransactionsModalProps> = ({ isOpen, onClose }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const limit = 20;

  const { data, isLoading, error, refetch } = useAdminTransactions({
    page: currentPage,
    limit,
    orderDirection: 'DESC',
  });

  if (!isOpen) return null;

  const transactions = data?.data || [];
  const filteredTransactions = transactions.filter(transaction => {
    if (!searchTerm) return true;
    const searchLower = searchTerm.toLowerCase();
    return (
      transaction.employeeName.toLowerCase().includes(searchLower) ||
      transaction.code.toLowerCase().includes(searchLower) ||
      transaction.type.toLowerCase().includes(searchLower)
    );
  });

  const columns: DataTableColumn<RecentTransaction & { initials: string }>[] = [
    {
      key: 'employee',
      label: 'Empleado',
      width: '30%',
      render: (_, row) => (
        <Avatar
          initials={row.initials}
          title={row.employeeName}
          subtitle={row.code}
          size="large"
          avatarBgColor="bg-brand-blue/30"
          avatarTextColor="text-brand-white"
        />
      ),
    },
    {
      key: 'type',
      label: 'Tipo',
      width: '25%',
      accessor: 'type',
    },
    {
      key: 'amount',
      label: 'Monto',
      width: '20%',
      render: (_, row) => <span className="font-medium">${row.amount.toLocaleString()}</span>,
    },
    {
      key: 'status',
      label: 'Estado',
      width: '15%',
      render: (_, row) => <Tag variant={getStatusVariant(row.status)}>{row.status}</Tag>,
    },
    {
      key: 'date',
      label: 'Fecha',
      width: '10%',
      render: (_, row) => (
        <span className="text-sm text-muted">
          {new Date(row.createdAt).toLocaleDateString('es-MX')}
        </span>
      ),
    },
  ];

  // Add initials to transactions
  const transactionsWithInitials = filteredTransactions.map(transaction => ({
    ...transaction,
    initials: getInitials(transaction.employeeName),
  }));

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <button
        className="absolute inset-0 bg-black opacity-60 cursor-default"
        onClick={onClose}
        onKeyDown={e => {
          if (e.key === 'Escape') onClose();
        }}
        aria-label="Close modal"
        type="button"
      />
      {/* Modal Content */}
      <div className="relative bg-surface border border-black/10 dark:border-white/15 rounded-lg shadow-2xl shadow-black/60 w-full max-w-6xl max-h-[90vh] overflow-hidden z-10">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div>
            <h2 className="text-xl font-bold text-brand-white">Historial Completo de Transacciones</h2>
            <p className="text-sm text-muted mt-1">Total de {data?.count} transacciones</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-muted hover:text-brand-white rounded-lg hover:bg-black/5 dark:hover:bg-white/5"
          >
            <Close />
          </button>
        </div>

        {/* Search Bar */}
        <div className="p-6 border-b border-border">
          <SearchBar
            value={searchTerm}
            onChange={setSearchTerm}
            placeholder="Buscar por empleado, código o tipo..."
            className="w-full"
          />
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto" style={{ maxHeight: 'calc(90vh - 280px)' }}>
          {isLoading ? (
            <LoadingState message="Cargando transacciones..." />
          ) : error ? (
            <ErrorState
              title="Error al cargar transacciones"
              message="No se pudieron cargar las transacciones. Por favor, intenta nuevamente."
              error={error}
              showRetry
              onRetry={() => void refetch()}
            />
          ) : transactionsWithInitials.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-muted">No se encontraron transacciones</p>
            </div>
          ) : (
            <DataTable
              columns={columns}
              data={transactionsWithInitials}
              onRowClick={_row => {
                // Handle transaction row click
              }}
            />
          )}
        </div>

        {/* Footer */}
        {data && data.count > limit && (
          <div className="flex items-center justify-between p-6 border-t border-border">
            <div className="text-sm text-muted">
              Página {currentPage} • Mostrando {transactionsWithInitials.length} de {data.count}
            </div>
            <div className="flex gap-2">
              <Button
                variant="white"
                size="small"
                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
              >
                Anterior
              </Button>
              <Button
                variant="white"
                size="small"
                onClick={() => setCurrentPage(prev => prev + 1)}
                disabled={currentPage * limit >= data.count}
              >
                Siguiente
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
